import TooltipWrapper from '@UI/New/TooltipWrapper/TooltipWrapper'
import { LoadingOutlined } from '@ant-design/icons'
import { CRM_INTEGRATION_CHAT_LAUNCHER_LABEL } from '@pages/Admin/CRM/components/PhoneIntegrationButtons/integrations'
import { formatCrmChatUnreadBadge } from '@pages/Admin/CRM/utils/leadFields'
import { useCrmIntegrationChatAvailability } from '@/hooks/useCrmIntegrationChatAvailability'
import { useCrmIntegrationChatUserAccess } from '@/hooks/useCrmIntegrationChatUserAccess'
import { useChatContext } from '@common/Chat/ChatContext'
import VkUnreadCountBadge from '@pages/Admin/CRM/components/integrations/vk/components/VkUnreadCountBadge'
import cn from 'classnames'
import type { MouseEvent } from 'react'
import { useIntegrationChatDock } from './integrationChatDockHooks'
import {
  CRM_CHAT_UNREAD_ALL_TOOLTIP,
  CRM_CHAT_UNREAD_MINE_TOOLTIP,
} from './integrationChatGlobalUnread'
import IntegrationChatDockLauncherProviders from './IntegrationChatDockLauncherProviders'
import classes from './IntegrationChatDock.module.css'

type IntegrationChatDockLauncherProps = {
  className?: string
}

const IntegrationChatDockLauncher = ({
  className,
}: IntegrationChatDockLauncherProps) => {
  const {
    isLoading: isProvidersLoading,
    hasAnyConnected,
    providers,
  } = useCrmIntegrationChatAvailability()
  const hasUserAccess = useCrmIntegrationChatUserAccess()
  const {
    expanded,
    launcherUnreadCount,
    launcherMyUnreadCount,
    toggleLauncher,
  } = useIntegrationChatDock()
  const chatContext = useChatContext()

  const canShowLauncher = hasUserAccess

  if (!canShowLauncher) {
    return null
  }

  const showAllBadge =
    !isProvidersLoading && hasAnyConnected && launcherUnreadCount > 0
  const showMineBadge =
    !isProvidersLoading && hasAnyConnected && launcherMyUnreadCount > 0
  const allBadgeLabel = showAllBadge
    ? formatCrmChatUnreadBadge(launcherUnreadCount)
    : ''
  const mineBadgeLabel = showMineBadge
    ? formatCrmChatUnreadBadge(launcherMyUnreadCount)
    : ''

  const handleClick = () => {
    if (isProvidersLoading) {
      return
    }

    chatContext?.changePage('default')
    toggleLauncher()
  }

  const stopBadgeMouseDown = (event: MouseEvent) => {
    event.stopPropagation()
  }

  return (
    <button
      type="button"
      className={cn(classes.launcher, className, {
        [classes.launcher_hasSession]: expanded && !isProvidersLoading,
        [classes.launcher_loading]: isProvidersLoading,
        [classes.launcher_hasProviders]:
          !isProvidersLoading && hasAnyConnected,
      })}
      aria-label={CRM_INTEGRATION_CHAT_LAUNCHER_LABEL}
      aria-busy={isProvidersLoading}
      disabled={isProvidersLoading}
      onClick={handleClick}
    >
      <span className={classes.launcherIconWrap}>
        {isProvidersLoading ? (
          <LoadingOutlined
            className={classes.launcherSpinner}
            spin
            onPointerEnterCapture={undefined}
            onPointerLeaveCapture={undefined}
          />
        ) : (
          <>
            <IntegrationChatDockLauncherProviders providers={providers} />
            {allBadgeLabel || mineBadgeLabel ? (
              <span
                className={classes.launcherUnreadBadges}
                onMouseDown={stopBadgeMouseDown}
                onClick={stopBadgeMouseDown}
              >
                {allBadgeLabel ? (
                  <TooltipWrapper title={CRM_CHAT_UNREAD_ALL_TOOLTIP}>
                    <span className={classes.unreadBadgeHit}>
                      <VkUnreadCountBadge
                        label={allBadgeLabel}
                        placement="inline"
                        variant="all"
                        className={classes.launcherUnreadBadgeCompact}
                        aria-label={`${CRM_CHAT_UNREAD_ALL_TOOLTIP}: ${launcherUnreadCount}`}
                      />
                    </span>
                  </TooltipWrapper>
                ) : null}
                {mineBadgeLabel ? (
                  <TooltipWrapper title={CRM_CHAT_UNREAD_MINE_TOOLTIP}>
                    <span className={classes.unreadBadgeHit}>
                      <VkUnreadCountBadge
                        label={mineBadgeLabel}
                        placement="inline"
                        variant="mine"
                        className={classes.launcherUnreadBadgeCompact}
                        aria-label={`${CRM_CHAT_UNREAD_MINE_TOOLTIP}: ${launcherMyUnreadCount}`}
                      />
                    </span>
                  </TooltipWrapper>
                ) : null}
              </span>
            ) : null}
          </>
        )}
      </span>
    </button>
  )
}

export default IntegrationChatDockLauncher
